import axiosInstance from '../../api';
import { isAxiosError } from 'axios';
import {
  HelpLinkDto,
  HelpLinkCreate,
  HelpLinkUpdate,
  SharepointColumnInfo,
  SharepointLibraryInfo,
  UserFeedbackConfigDto,
  BreaktimeAnnouncementConfig,
  BreaktimeAnnouncementConfigInput,
  BreaktimeAnnouncementConfigSummary,
} from './types';

/**
 * API call to delete an existing help link
 * @param id The ID of the help link to delete
 */
export const deleteHelpLink = async (id: string): Promise<string> => {
  const response = await axiosInstance.delete(`/api/App/helpLink/${id}`);
  return response.data;
};

/**
 * API call to create a new help link
 * @param helpLinkCreate The data for creating the help link
 */
export const createHelpLink = async (
  helpLinkCreate: HelpLinkCreate,
): Promise<HelpLinkDto> => {
  const response = await axiosInstance.post<HelpLinkDto>(
    '/api/App/helpLink',
    helpLinkCreate,
  );
  return response.data;
};

/**
 * API call to get help links
 */
export const getHelpLinks = async (): Promise<Array<HelpLinkDto>> => {
  const response =
    await axiosInstance.get<Array<HelpLinkDto>>('/api/App/helpLink');
  return response.data;
};

/**
 * API call to update an existing help link
 * @param params
 */
export const updateHelpLink = async (
  params: HelpLinkUpdate & { id: string },
): Promise<HelpLinkDto> => {
  const { id, ...info } = params;
  const response = await axiosInstance.post(`/api/App/helpLink/${id}`, info);
  return response.data;
};

/**
 * API call to get user feedback configurations
 */
export const getUserFeedbackConfig = async (): Promise<
  Array<UserFeedbackConfigDto>
> => {
  const response = await axiosInstance.get<Array<UserFeedbackConfigDto>>(
    '/api/App/feedback/config',
  );
  return response.data;
};

/**
 * API call to create a new user feedback configuration
 * @param configInfo The configuration data to create
 */
export const createUserFeedbackConfig = async (
  configInfo: Omit<UserFeedbackConfigDto, 'id'>,
): Promise<UserFeedbackConfigDto> => {
  const response = await axiosInstance.post<UserFeedbackConfigDto>(
    '/api/App/feedback/config',
    configInfo,
  );
  return response.data;
};

/**
 * API call to update an existing user feedback configuration
 * @param config The configuration to update
 */
export const updateUserFeedbackConfig = async (
  config: UserFeedbackConfigDto,
): Promise<UserFeedbackConfigDto> => {
  const response = await axiosInstance.put<UserFeedbackConfigDto>(
    `/api/App/feedback/config/${config.id}`,
    config,
  );
  return response.data;
};

/**
 * API call to delete a user feedback configuration
 * @param id The ID of the configuration to delete
 */
export const deleteUserFeedbackConfig = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/api/App/feedback/config/${id}`);
};

/**
 * API call to fetch SharePoint libraries from backend proxy.
 */
export const getSharepointLibraries = async (): Promise<
  SharepointLibraryInfo[]
> => {
  const response = await axiosInstance.get<SharepointLibraryInfo[]>(
    '/api/Sharepoint/libraries',
  );
  return response.data;
};

/**
 * API call to fetch SharePoint columns for a specific library/list.
 */
export const getSharepointColumns = async (
  listName: string,
): Promise<SharepointColumnInfo[]> => {
  const response = await axiosInstance.get<SharepointColumnInfo[]>(
    '/api/Sharepoint/columns',
    {
      params: { listName },
    },
  );
  return response.data;
};

/**
 * API call to fetch breaktime announcement summaries for all inventory centers.
 */
export const getBreaktimeAnnouncementSummaries = async (): Promise<
  BreaktimeAnnouncementConfigSummary[]
> => {
  const response = await axiosInstance.get<
    BreaktimeAnnouncementConfigSummary[]
  >('/api/ManufacturingLocation/inventoryCenter/breaktime-announcements');
  return response.data;
};

/**
 * API call to fetch breaktime announcement configuration for an inventory center.
 */
export const getBreaktimeAnnouncementConfig = async (
  inventoryCenterId: string,
): Promise<BreaktimeAnnouncementConfig | null> => {
  try {
    const response = await axiosInstance.get<BreaktimeAnnouncementConfig>(
      `/api/ManufacturingLocation/inventoryCenter/${inventoryCenterId}/breaktime-announcement`,
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    throw error;
  }
};

/**
 * API call to upsert breaktime announcement configuration for an inventory center.
 */
export const upsertBreaktimeAnnouncementConfig = async (param: {
  inventoryCenterId: string;
  input: BreaktimeAnnouncementConfigInput;
}): Promise<BreaktimeAnnouncementConfig> => {
  const response = await axiosInstance.put<BreaktimeAnnouncementConfig>(
    `/api/ManufacturingLocation/inventoryCenter/${param.inventoryCenterId}/breaktime-announcement`,
    param.input,
  );
  return response.data;
};

/**
 * API call to delete breaktime announcement configuration for an inventory center.
 */
export const deleteBreaktimeAnnouncementConfig = async (
  inventoryCenterId: string,
): Promise<void> => {
  await axiosInstance.delete(
    `/api/ManufacturingLocation/inventoryCenter/${inventoryCenterId}/breaktime-announcement`,
  );
};
